import { Button, ButtonColors } from '@chiroup/components/Button';
import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { LoadingPage } from '@chiroup/components/LoadingPage';
import {
  DejavooDiagnosticFinding,
  DejavooDiagnosticSeverity,
} from '@chiroup/core/types/Devtools.type';
import React, { use, useEffect, useMemo, useState } from 'react';
import { ToastContext, ToastTypes } from '../../../contexts/toast.context';
import { useAdminPermissions } from '../../../hooks/useAdminPermissions';
import {
  useDeepDejavooDiagnosis,
  useDejavooDiagnosticAutofix,
  useDejavooDiagnostics,
  useProcessDejavooDiagnostics,
  useStartDejavooDiagnostics,
} from '../../../hooks/useDejavooDiagnostics';
import Header from '../../layout/Header';

const inputClass =
  'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-dark-gray-600 dark:bg-dark-gray-800 dark:text-white';
const thClass = 'px-3 py-3 text-left text-sm font-semibold text-gray-900 dark:text-dark-gray-100';
const tdClass = 'px-3 py-3 text-sm text-gray-700 dark:text-dark-gray-300 align-top';

const formatDateTime = (value?: number | null) =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    : '—';

const formatMoney = (value: number) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value);

const errorMessage = (error: unknown): string => {
  const requestError = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };

  return (
    requestError.response?.data?.message || requestError.message || 'An unexpected error occurred.'
  );
};

const badgeClass: Record<DejavooDiagnosticSeverity, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200',
};

const SeverityBadge: React.FC<{ finding: DejavooDiagnosticFinding }> = ({ finding }) => (
  <span
    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass[finding.severity]}`}
  >
    {finding.severity === 'resolved'
      ? 'Resolved'
      : finding.severity.charAt(0).toUpperCase() + finding.severity.slice(1)}
  </span>
);

const DejavooDiagnostics: React.FC = () => {
  const { createToast } = use(ToastContext);
  const { hasPermission, isLoading: permissionsLoading } = useAdminPermissions();
  const canView = hasPermission('devtools.dejavoo_diagnostics');
  const canAutofix = hasPermission('devtools.dejavoo_autofix');
  const diagnostics = useDejavooDiagnostics(canView);
  const start = useStartDejavooDiagnostics();
  const process = useProcessDejavooDiagnostics();
  const deepDiagnose = useDeepDejavooDiagnosis();
  const autofix = useDejavooDiagnosticAutofix();
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState<'all' | DejavooDiagnosticSeverity>('all');
  const [selectedKey, setSelectedKey] = useState<string>();
  const [repairKey, setRepairKey] = useState<string>();

  const run = diagnostics.data?.run;
  const findings = useMemo(() => run?.findings ?? [], [run?.findings]);
  const findingKey = (finding: DejavooDiagnosticFinding) =>
    `${finding.clinicId}:${finding.transactionId}`;
  const pendingRepair = findings.find(finding => findingKey(finding) === repairKey);

  useEffect(() => {
    if (!run || run.status !== 'running' || process.isLoading || process.isError) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => process.mutate({ fn: 'process', runId: run.id }),
      diagnostics.data?.retryAfterMs ?? 150
    );

    return () => window.clearTimeout(timeoutId);
  }, [
    diagnostics.data?.retryAfterMs,
    process,
    process.isError,
    process.isLoading,
    run,
    run?.id,
    run?.processedCount,
    run?.status,
  ]);

  const metrics = useMemo(
    () => ({
      total: findings.length,
      critical: findings.filter(finding => finding.severity === 'critical').length,
      review: findings.filter(finding => finding.severity === 'warning').length,
      inconclusive: findings.filter(finding => finding.diagnosis === 'inconclusive').length,
      autofixable: findings.filter(finding => finding.autofix).length,
    }),
    [findings]
  );

  const filteredFindings = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return findings.filter(finding => {
      if (severity !== 'all' && finding.severity !== severity) return false;
      if (!normalizedSearch) return true;

      return [
        finding.transactionId,
        finding.clinicId,
        finding.externalReference,
        finding.summary,
        finding.diagnosis,
      ].some(value =>
        String(value || '')
          .toLowerCase()
          .includes(normalizedSearch)
      );
    });
  }, [findings, search, severity]);

  const notifyFailure = (title: string, error: unknown) =>
    createToast({
      title,
      description: errorMessage(error),
      type: ToastTypes.Fail,
      duration: 7000,
    });

  const startOrResume = async () => {
    if (run?.status === 'running') {
      process.reset();
      process.mutate({ fn: 'process', runId: run.id });
      return;
    }

    try {
      await start.mutateAsync({ fn: 'start' });
    } catch (error) {
      notifyFailure('Could not start Dejavoo diagnostics', error);
    }
  };

  const runDeepDiagnosis = async (finding: DejavooDiagnosticFinding) => {
    if (!run) return;
    try {
      await deepDiagnose.mutateAsync({
        fn: 'deepDiagnose',
        runId: run.id,
        transactionId: finding.transactionId,
        clinicId: finding.clinicId,
      });
      createToast({
        title: 'Deep diagnosis complete',
        description: 'The finding now includes the settled-batch check when it was needed.',
        type: ToastTypes.Success,
        duration: 5000,
      });
    } catch (error) {
      notifyFailure('Deep diagnosis failed', error);
    }
  };

  const confirmRepair = async () => {
    if (!run || !pendingRepair?.autofix) return;
    try {
      await autofix.mutateAsync({
        fn: 'autofix',
        runId: run.id,
        transactionId: pendingRepair.transactionId,
        clinicId: pendingRepair.clinicId,
        action: pendingRepair.autofix,
        confirmed: true,
      });
      setRepairKey(undefined);
      createToast({
        title: 'Dejavoo repair completed',
        description:
          pendingRepair.autofix === 'reconcile_payment'
            ? 'The missing ledger payment was created without charging the card again.'
            : 'The processor-confirmed stale pending row was dismissed.',
        type: ToastTypes.Success,
        duration: 7000,
      });
    } catch (error) {
      notifyFailure('Repair stopped safely', error);
    }
  };

  if (permissionsLoading || diagnostics.isLoading) return <LoadingPage />;

  const now = Date.now();
  const refreshBlocked = Boolean(run && run.status !== 'running' && run.nextAllowedAt > now);
  const primaryText =
    run?.status === 'running'
      ? process.isError
        ? 'Resume scan'
        : `Scanning ${run.processedCount} of ${run.totalCount}`
      : refreshBlocked
        ? `Refresh available ${formatDateTime(run?.nextAllowedAt)}`
        : run
          ? 'Refresh diagnosis'
          : 'Run diagnosis';
  return (
    <>
      <Header
        title="Dejavoo diagnostics"
        rightSide={
          <Button
            text={primaryText}
            onClick={startOrResume}
            loading={start.isLoading || (run?.status === 'running' && process.isLoading)}
            disabled={refreshBlocked || (run?.status === 'running' && !process.isError)}
          />
        }
      >
        <p className="mt-2 max-w-4xl text-sm text-gray-600 dark:text-dark-gray-300">
          Find unlinked terminal sales across every clinic, explain the evidence, and preview safe
          repairs. Scans are limited to once per hour and reuse unchanged processor checks for 24
          hours.
        </p>
      </Header>

      <main className="space-y-6 p-4 sm:p-6">
        {process.isError && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
            The scan paused: {errorMessage(process.error)}. No rows were changed. Use Resume scan to
            continue the cached run.
          </div>
        )}

        {!run ? (
          <section className="max-w-3xl py-12">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              No diagnostic run yet
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-dark-gray-300">
              Run the first scan to check unlinked approved, pending, and previously dismissed
              Dejavoo sales. The results contain processor identifiers only, not patient data.
            </p>
          </section>
        ) : (
          <>
            <section className="border-b border-gray-200 pb-5 dark:border-dark-gray-700">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-5">
                {[
                  ['Transactions', metrics.total],
                  ['Critical', metrics.critical],
                  ['Needs review', metrics.review],
                  ['Inconclusive', metrics.inconclusive],
                  ['Autofixable', metrics.autofixable],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-gray-500 dark:text-dark-gray-400">{label}</dt>
                    <dd className="mt-1 text-2xl font-semibold tabular-nums text-gray-900 dark:text-white">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="mt-4 text-xs text-gray-500 dark:text-dark-gray-400">
                Started {formatDateTime(run.startedAt)} ·{' '}
                {run.cached ? 'Cached result' : 'Fresh result'}
                {' · '}Next full scan {formatDateTime(run.nextAllowedAt)}
              </p>
            </section>

            <section>
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_220px]">
                <div>
                  <label htmlFor="dejavoo-diagnostic-search" className="sr-only">
                    Search findings
                  </label>
                  <input
                    id="dejavoo-diagnostic-search"
                    name="search"
                    type="search"
                    className={inputClass}
                    placeholder="Search transaction, clinic, external reference, or diagnosis"
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="dejavoo-severity" className="sr-only">
                    Filter by severity
                  </label>
                  <select
                    id="dejavoo-severity"
                    name="severity"
                    className={inputClass}
                    value={severity}
                    onChange={event =>
                      setSeverity(event.target.value as 'all' | DejavooDiagnosticSeverity)
                    }
                  >
                    <option value="all">All severities</option>
                    <option value="critical">Critical</option>
                    <option value="warning">Needs review</option>
                    <option value="info">Information</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-gray-700">
                  <thead>
                    <tr>
                      <th className={thClass}>Finding</th>
                      <th className={thClass}>Clinic</th>
                      <th className={thClass}>Transaction</th>
                      <th className={thClass}>Amount</th>
                      <th className={thClass}>Created</th>
                      <th className={`${thClass} text-right`}>Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-dark-gray-700">
                    {filteredFindings.map(finding => {
                      const key = findingKey(finding);
                      const isSelected = selectedKey === key;
                      const deepBlockedUntil = finding.deepDiagnosedAt
                        ? finding.deepDiagnosedAt + 60 * 60 * 1000
                        : 0;

                      return (
                        <React.Fragment key={key}>
                          <tr>
                            <td className={`${tdClass} max-w-xl`}>
                              <div className="flex flex-wrap items-center gap-2">
                                <SeverityBadge finding={finding} />
                                {finding.autofix && (
                                  <span className="text-xs font-medium text-primary-700 dark:text-primary-300">
                                    Autofix available
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 font-medium text-gray-900 dark:text-white">
                                {finding.summary}
                              </div>
                            </td>
                            <td className={`${tdClass} tabular-nums`}>{finding.clinicId}</td>
                            <td className={`${tdClass} tabular-nums`}>{finding.transactionId}</td>
                            <td className={`${tdClass} tabular-nums`}>
                              {formatMoney(finding.amount)}
                            </td>
                            <td className={`${tdClass} whitespace-nowrap`}>
                              {formatDateTime(finding.createdAt)}
                            </td>
                            <td className={`${tdClass} text-right`}>
                              <Button
                                text={isSelected ? 'Close' : 'View'}
                                size="sm"
                                color={ButtonColors.plainWithBorder}
                                onClick={() => setSelectedKey(isSelected ? undefined : key)}
                              />
                            </td>
                          </tr>
                          {isSelected && (
                            <tr>
                              <td
                                colSpan={6}
                                className="bg-gray-50 px-3 py-5 dark:bg-dark-gray-900/30"
                              >
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <SeverityBadge finding={finding} />
                                      <span className="text-sm tabular-nums text-gray-500 dark:text-dark-gray-400">
                                        Clinic {finding.clinicId} · Transaction{' '}
                                        {finding.transactionId}
                                      </span>
                                    </div>
                                    <h2 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
                                      {finding.summary}
                                    </h2>
                                  </div>
                                  <button
                                    type="button"
                                    className="text-left text-sm text-gray-500 hover:text-gray-900 sm:text-right dark:text-dark-gray-400 dark:hover:text-white"
                                    onClick={() => setSelectedKey(undefined)}
                                  >
                                    Close details
                                  </button>
                                </div>

                                <dl className="mt-6 grid gap-5 lg:grid-cols-3">
                                  <div>
                                    <dt className="text-sm font-semibold text-gray-900 dark:text-white">
                                      What happened
                                    </dt>
                                    <dd className="mt-1 text-sm leading-6 text-gray-600 dark:text-dark-gray-300">
                                      {finding.explanation}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="text-sm font-semibold text-gray-900 dark:text-white">
                                      Recommended next step
                                    </dt>
                                    <dd className="mt-1 text-sm leading-6 text-gray-600 dark:text-dark-gray-300">
                                      {finding.recommendedAction}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="text-sm font-semibold text-gray-900 dark:text-white">
                                      Processor evidence
                                    </dt>
                                    <dd className="mt-1 space-y-1 text-sm text-gray-600 dark:text-dark-gray-300">
                                      <div>
                                        External reference: {finding.externalReference || 'Missing'}
                                      </div>
                                      <div>
                                        Status code: {finding.processorStatusCode || 'Not returned'}
                                      </div>
                                      <div>
                                        Result code: {finding.processorResultCode || 'Not returned'}
                                      </div>
                                      <div>Checked: {formatDateTime(finding.checkedAt)}</div>
                                      <div>
                                        {finding.processorMessage ||
                                          'No processor message was returned.'}
                                      </div>
                                    </dd>
                                  </div>
                                </dl>

                                <div className="mt-6 flex flex-wrap gap-3">
                                  {finding.diagnosis === 'inconclusive' && (
                                    <Button
                                      text={
                                        deepBlockedUntil > now
                                          ? `Deep check available ${formatDateTime(deepBlockedUntil)}`
                                          : 'Run deep diagnosis'
                                      }
                                      color={ButtonColors.plainWithBorder}
                                      loading={deepDiagnose.isLoading}
                                      disabled={deepBlockedUntil > now}
                                      onClick={() => runDeepDiagnosis(finding)}
                                    />
                                  )}
                                  {finding.autofix && canAutofix && (
                                    <Button
                                      text="Preview autofix"
                                      color={ButtonColors.orangeBorder}
                                      onClick={() => setRepairKey(key)}
                                    />
                                  )}
                                  {finding.autofix && !canAutofix && (
                                    <span className="text-sm text-gray-500 dark:text-dark-gray-400">
                                      You can review this autofix but do not have permission to run
                                      it.
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {filteredFindings.length === 0 && (
                      <tr>
                        <td className={tdClass} colSpan={6}>
                          No findings match these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </main>

      <ConfirmModal
        isOpen={Boolean(pendingRepair)}
        close={() => setRepairKey(undefined)}
        confirm={confirmRepair}
        loading={autofix.isLoading}
        focusOnCancel
        bigger
        title={
          pendingRepair?.autofix === 'reconcile_payment'
            ? 'Create the missing ledger payment?'
            : 'Dismiss this stale pending row?'
        }
        confirmText={
          pendingRepair?.autofix === 'reconcile_payment'
            ? 'Create ledger payment'
            : 'Dismiss pending row'
        }
        description={
          pendingRepair?.autofix === 'reconcile_payment'
            ? 'This does not charge the card. Before writing, the backend will recheck Dejavoo, confirm every original allocation is still payable, and stop if any plausible ledger payment already exists.'
            : 'Before changing the row, the backend will recheck that Dejavoo explicitly confirms no approved sale. An inconclusive lookup can never dismiss it.'
        }
      >
        {pendingRepair && (
          <div className="space-y-3 text-sm text-gray-700 dark:text-dark-gray-300">
            <div className="grid grid-cols-2 gap-3 border-y border-gray-200 py-3 dark:border-dark-gray-700">
              <div>
                <div className="text-xs text-gray-500">Clinic</div>
                <div className="font-medium tabular-nums">{pendingRepair.clinicId}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Transaction</div>
                <div className="font-medium tabular-nums">{pendingRepair.transactionId}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Amount</div>
                <div className="font-medium tabular-nums">{formatMoney(pendingRepair.amount)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">External reference</div>
                <div className="font-medium break-all">{pendingRepair.externalReference}</div>
              </div>
            </div>
            <p>
              <strong>Why this is available:</strong> {pendingRepair.explanation}
            </p>
            <p>
              <strong>Afterward:</strong> {pendingRepair.recommendedAction}
            </p>
          </div>
        )}
      </ConfirmModal>
    </>
  );
};

export default DejavooDiagnostics;
